import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { has, head, pathOr, groupBy, propOr } from 'ramda'
import { useSelector, useMakeSelector } from 'hooks/redux'

import strings from 'utils/strings'

import { updateFragments, resetFragments } from 'actions/powerTakeOff'

import { makeHasRights } from 'selectors/authorization'
import { getInDirtyState, getInErrorState } from 'selectors/validation'
import {
  getDirtyFragments,
  makeGetFragment,
  getGroupFragmentsWithBoundsFragments,
  getIsPtoEditBlocked,
  getFragmentDomainName,
  getDomainsList,
} from 'selectors/powerTakeOff'

import { UPDATE_PLUS_PTO } from 'constants/authorization'

import { Header } from 'components/patterns'

import SingleEditor from './SingleEditor'
import GroupEditor from './GroupEditor'

import { StyledDomainParagraph, StyledEditor } from './FragmentEditor.style'

const FragmentEditor = ({ id }) => {
  const dispatch = useDispatch()
  const inDirtyState = useSelector(getInDirtyState)
  const inErrorState = useSelector(getInErrorState)
  const dirtyFragments = useSelector(getDirtyFragments)
  const domainName = useSelector(getFragmentDomainName, { id })
  const domains = useSelector(getDomainsList)
  const fragment = useMakeSelector(makeGetFragment, { id })
  const canUpdatePto = useMakeSelector(makeHasRights, {
    rights: [UPDATE_PLUS_PTO],
  })
  const isPtoEditBlocked = useSelector(getIsPtoEditBlocked)

  const { name: fragmentName, groupId } = fragment
  const { fragments, enables, enabledBy, lowerBound, upperBound } = useSelector(
    getGroupFragmentsWithBoundsFragments,
    {
      groupId,
    }
  )
  const isGroupEditor = has('groupId', fragment)
  const controlFragmentRefNo = head(enabledBy)
  // show value setting name as title when config prop empty in group editor
  const groupHeaderTitle = pathOr(
    fragmentName,
    [controlFragmentRefNo, 'name'],
    fragments
  )

  const handleSave = useCallback(
    (notes, callback) => {
      dispatch(updateFragments(notes, callback))
    },
    [dispatch]
  )

  const handleReset = useCallback(
    (callback) => {
      const ids = dirtyFragments.map((f) => f.refNo)
      dispatch(resetFragments(ids))
      callback()
    },
    [dispatch, dirtyFragments]
  )

  const { true: outOfRange = [], false: inRange = [] } = useMemo(
    () => groupBy(propOr(false, 'isEcfFragmentOutOfRange'), dirtyFragments),
    [dirtyFragments]
  )
  const listItemsInRange = useMemo(
    () =>
      inRange.reduce((prev, itm) => {
        const existingDomainIdx = prev.findIndex((p) => p.domain === itm.domain)
        if (existingDomainIdx !== -1) {
          prev[existingDomainIdx].names.push(itm.name)
          return prev
        }
        return [
          ...prev,
          {
            domain: itm.domain,
            domainName: domains.find((domain) => domain.id === itm.domain)
              ?.name,
            names: [itm.name],
          },
        ]
      }, []),
    [inRange, domains]
  )

  const listItemsOutOfRange = useMemo(
    () =>
      outOfRange.reduce((prev, itm) => {
        const existingDomainIdx = prev.findIndex((p) => p.domain === itm.domain)
        if (existingDomainIdx !== -1) {
          prev[existingDomainIdx].names.push(itm.name)
          return prev
        }
        return [
          ...prev,
          {
            domain: itm.domain,
            domainName: domains.find((domain) => domain.id === itm.domain)
              ?.name,
            names: [itm.name],
          },
        ]
      }, []),
    [outOfRange, domains]
  )

  const itemsWithHeading = listItemsOutOfRange
    .map((itm) => ({
      heading: itm.domainName,
      subHeader: strings.updated_the_following_out_of_range_settings,
      items: itm.names,
    }))
    .concat(
      listItemsInRange.map((itm) => ({
        heading: itm.domainName,
        subHeader: strings.updated_the_following_settings,
        items: itm.names,
      }))
    )

  return (
    <StyledEditor
      active={canUpdatePto && !isPtoEditBlocked}
      actions={{
        onSave: handleSave,
        onReset: handleReset,
      }}
      overlayContent={{
        save: {
          heading: strings.edit_pto_domain,
          lists: itemsWithHeading,
        },
        reset: {
          heading: strings.reset_pto_settings,
          subheading: strings.reset_pto_settings_description,
          lists: itemsWithHeading,
        },
      }}
      hasModifications={inDirtyState}
      hasErrors={inErrorState}
    >
      <StyledDomainParagraph>{domainName}</StyledDomainParagraph>
      <Header type="h2">
        {isGroupEditor ? groupHeaderTitle : fragmentName}
      </Header>

      {isGroupEditor ? (
        <GroupEditor
          {...{
            fragments,
            enables,
            enabledBy,
            lowerBound,
            upperBound,
          }}
        />
      ) : (
        <SingleEditor fragment={fragment} />
      )}
    </StyledEditor>
  )
}

FragmentEditor.propTypes = {
  id: PropTypes.string,
}

export default FragmentEditor
